import React, { useEffect, useState } from 'react';
import { sbomApi } from './sbomApi';

const KIND = {
  added: ['Added', 'ok'], changed: ['New version', 'info'], removed: ['Removed', 'bad'], same: ['Unchanged', 'plain'],
};

/** Each release's current SBOM, then the baselines it was updated from (`b<id>` — the tree before
 *  an import replaced it, sbom_baselines). */
function sides(releases) {
  const out = [];
  releases.forEach((r) => {
    out.push({ value: r.item_id, label: r.name || r.item_id });
    (r.baselines || []).forEach((bl) => out.push({
      value: `b${bl.id}`, label: `${r.name} — before ${String(bl.replaced_at || '').slice(0, 10)} (${bl.file_name || 'baseline'})`,
    }));
  });
  return out;
}

/** The release grid shows a release's CURRENT tree, so a row is located on the To side when the
 *  package is there, else on the From side — never in a baseline (`b<id>`), which has no grid. */
function locateTarget(r, a, b) {
  const isRel = (x) => x && !String(x).startsWith('b');
  if (isRel(b) && r.after_ids?.length) return { rel: b, ids: r.after_ids };
  if (isRel(a) && r.before_ids?.length) return { rel: a, ids: r.before_ids };
  return null;
}

/** Two SBOMs (releases or baselines), package by package. `initial` reopens on the last pair
 *  after a Locate; `onLocate(rel, ids, label, {a, b})` shows the package in the release grid. */
export default function SbomCompareModal({ releases, initial, onClose, onAuth, onLocate }) {
  const opts = sides(releases);
  const firstBase = releases.find((r) => r.baselines?.length);
  const [a, setA] = useState(initial?.a || releases[1]?.item_id || (firstBase ? `b${firstBase.baselines[0].id}` : ''));
  const [b, setB] = useState(initial?.b || (releases[1] || !firstBase ? (releases[0]?.item_id || '') : firstBase.item_id));
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [showSame, setShowSame] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (!a || !b || a === b) { setRows(null); return; }
    let off = false;
    setError('');
    sbomApi.compare(a, b)
      .then((d) => { if (!off) setRows(d.rows || []); })
      .catch((e) => { if (!off && !onAuth(e)) setError(e.message); });
    return () => { off = true; };
  }, [a, b, onAuth]);

  const name = (id) => opts.find((o) => o.value === id)?.label || id;
  const count = (k) => (rows || []).filter((r) => r.kind === k).length;
  const shown = (rows || []).filter((r) => showSame || r.kind !== 'same');

  return (
    <div className="sbom-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <section className="sbom-modal" style={{ width: 'min(760px, 100%)' }} role="dialog" aria-modal="true" aria-labelledby="sbom-cmp-title">
        <div className="sbom-modal-head">
          <h3 id="sbom-cmp-title">Compare releases</h3>
          <button type="button" className="sbom-modal-x" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <div className="sbom-modal-body">
          <div className="sbom-grid2">
            <label className="sbom-field">From
              <select className="sbom-select" value={a} onChange={(e) => setA(e.target.value)}>
                {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <label className="sbom-field">To
              <select className="sbom-select" value={b} onChange={(e) => setB(e.target.value)}>
                {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
          </div>
          {a === b && <div className="sbom-note">Pick two different SBOMs.</div>}
          {error && <div className="sbom-error" role="alert">{error}</div>}
          {rows && (
            <>
              <div className="sbom-diffn">
                {Object.entries(KIND).map(([k, [l]]) => (
                  <div key={k} className="sbom-stat"><div className="sbom-stat-n">{count(k)}</div><div className="sbom-stat-l">{l}</div></div>
                ))}
              </div>
              <label className="sbom-tiny"><input type="checkbox" checked={showSame} onChange={(e) => setShowSame(e.target.checked)} /> Show unchanged packages</label>
              <div className="sbom-tw" style={{ maxHeight: 380, overflowY: 'auto' }}>
                <table className="sbom-table">
                  <thead><tr><th>Change</th><th>Package</th><th>{name(a)}</th><th>{name(b)}</th><th /></tr></thead>
                  <tbody>
                    {shown.map((r) => {
                      const t = onLocate && locateTarget(r, a, b);
                      return (
                        <tr key={r.key || r.package}>
                          <td><span className={`sbom-pill sbom-pill--${KIND[r.kind][1]}`}>{KIND[r.kind][0]}</span></td>
                          <td>{r.package}</td><td className="sbom-mono">{r.before || '—'}</td><td className="sbom-mono">{r.after || '—'}</td>
                          <td className="sbom-icon-acts">
                            {t && (
                              <button type="button" className="sbom-act" title={`Show ${r.package} in the ${name(t.rel)} grid`}
                                onClick={() => onLocate(t.rel, t.ids, r.package, { a, b })}>Locate</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {!shown.length && <tr><td colSpan={5} className="sbom-empty">No differences.</td></tr>}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
